/** Angular Imports */
import { isPlatformBrowser } from '@angular/common';
import {
    Component,
    DestroyRef,
    OnDestroy,
    OnInit,
    PLATFORM_ID,
    computed,
    inject,
    signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { Button } from '@ntv360/component-pantry';
import { switchMap } from 'rxjs';

/** Local Imports */
import { AuthService } from '@core';

const RESEND_COOLDOWN = 5 * 60;

@Component({
    selector: 'app-login',
    imports: [Button],
    templateUrl: './login.component.html',
    styleUrl: './login.component.scss',
    standalone: true,
})
export class LoginComponent implements OnInit, OnDestroy {
    /** Handles authentication flows (login, resend verification, session-status check). */
    private readonly _authService = inject(AuthService);

    /** Provides access to the current route's query parameters. */
    private readonly _route = inject(ActivatedRoute);

    /** Used to redirect to dedicated error pages (e.g. `/block`) when needed. */
    private readonly _router = inject(Router);

    /** Used to check whether code is running in a browser vs. server (SSR). */
    private readonly _platformId = inject(PLATFORM_ID);

    /** Ties subscriptions to the component lifecycle to prevent memory leaks. */
    private readonly _destroyRef = inject(DestroyRef);

    /** Whether to show the expired verification link UI (resend flow). */
    protected showExpiredLink = signal(false);

    /** Whether to show the "Session Expired — Return to Login" card. */
    protected showSessionExpired = signal(false);

    /** Auth0 returned `This URL can be used only once` — verification link already consumed. */
    protected showLinkUsedOnce = signal(false);

    /** Auth0 returned `code=already-verified` — account verified on a previous click. */
    protected showAlreadyVerified = signal(false);

    /** Auth0 password-reset success — prompts user to sign in with the new password. */
    protected showPasswordResetSuccess = signal(false);

    /**
     * True while the backend session-status check is in flight after an `Access expired.` redirect.
     * Suppresses flicker between the verification-link card and the session-expired card.
     */
    protected sessionCheckLoading = signal(false);

    /** Email address the verification link was sent to, sourced from sessionStorage user traits. */
    protected userEmail = signal('');

    /** Remaining cooldown seconds before the resend button is re-enabled. */
    protected secondsLeft = signal(0);

    /** Total seconds for the active cooldown window — used as the progress denominator. */
    protected initialSeconds = signal(RESEND_COOLDOWN);

    /** True once the countdown reaches zero. */
    protected timerExpired = signal(false);

    /** True while the cooldown is running; used to disable the resend button. */
    protected timerActive = computed(() => this.secondsLeft() > 0);

    /** MM:SS string derived from `secondsLeft` for display in the template. */
    protected timerDisplay = computed(() => {
        const s = this.secondsLeft();
        const m = Math.floor(s / 60)
            .toString()
            .padStart(2, '0');
        const sec = (s % 60).toString().padStart(2, '0');
        return `${m}:${sec}`;
    });

    /** Reference to the active countdown interval, or null when no timer is running. */
    private _timerInterval: ReturnType<typeof setInterval> | null = null;

    /** Toast visibility flag — toggled true to render the error toast, false to hide. */
    protected showErrorToast = signal(false);

    /** Toast message text rendered inside the error toast. */
    protected errorToastMessage = signal('');

    ngOnInit(): void {
        const success = this._route.snapshot.queryParamMap.get('success');
        const message = this._route.snapshot.queryParamMap.get('message');
        const error = this._route.snapshot.queryParamMap.get('error');
        const errorDescription = this._route.snapshot.queryParamMap.get('error_description');
        const code = this._route.snapshot.queryParamMap.get('code');
        // Auth0's password-reset success redirect lands here with only `?iss=...` —
        // no success/code/message/error. `iss` is the OIDC issuer and is the only
        // signal we get that the user is bouncing back from the hosted reset page.
        const iss = this._route.snapshot.queryParamMap.get('iss');
        const isPasswordResetReturn = !!iss && !success && !code && !message && !error;

        if (this.isRateLimitedError(error, errorDescription)) {
            this._router.navigate(['/block'], { replaceUrl: true });
            return;
        }

        // Reaching the login page implies the user wants a fresh session. Any other
        // authenticated tab on this origin must surface the cross-tab logout notice
        // modal via BroadcastChannel.
        //
        // If we arrived here via an Auth0 redirect (any of success/code/message/error/iss)
        // AND the browser still carries credentials from a prior login, that session
        // belongs to a different user than the one this URL targets — hit the BFF
        // logout to kill the HttpOnly cookie server-side. Skip the Auth0 SSO redirect
        // (`logout()` would bounce through Auth0 and lose the redirect context).
        if (isPlatformBrowser(this._platformId)) {
            this._authService.broadcastLogout();

            if (isPasswordResetReturn) {
                // Auth0 password-reset success bounce — terminate SSO session and
                // land on clean /login (no `iss` query) so user must re-authenticate.
                this.sessionCheckLoading.set(true);
                this._authService.logout().pipe(takeUntilDestroyed(this._destroyRef)).subscribe();
                return;
            }

            const hasLocalAuth = !!localStorage.getItem('ntv_user_traits');
            const isAuth0Redirect = !!(success || code || message || error || iss);

            if (hasLocalAuth && isAuth0Redirect) {
                // Clear local state synchronously so the auth signals and localStorage
                // reflect logged-out immediately — before the async BFF call completes.
                // This prevents the race window where the admin could still reach the
                // dashboard between now and when serverLogout()'s tap() fires.
                this._authService.clearAuthentication();
                this._authService.serverLogout().subscribe();
            } else if (hasLocalAuth) {
                this._authService.clearAuthentication();
            }
        }

        if (success === 'true' && isPlatformBrowser(this._platformId)) {
            sessionStorage.removeItem('ntv_pending_verification');
        }

        if (error) {
            this.showAuthErrorToast(error);
        }

        if (success === 'true' && this.isPasswordResetMessage(message)) {
            this.showPasswordResetSuccess.set(true);
            return;
        }

        if (success === 'false' && code === 'already-verified') {
            this.showAlreadyVerified.set(true);
            return;
        }

        if (success === 'false' && message === 'This URL can be used only once') {
            this.showLinkUsedOnce.set(true);
            return;
        }

        if (success === 'false' && message === 'Access expired.') {
            // Probe the backend for the HttpOnly `__Host-session_id` cookie before deciding
            // which UI to render. With a session → resend flow; without → return-to-login card.
            this.sessionCheckLoading.set(true);
            this._authService
                .getSessionStatus()
                .pipe(takeUntilDestroyed(this._destroyRef))
                .subscribe((res) => {
                    this.sessionCheckLoading.set(false);
                    if (res?.hasSession) {
                        this.startVerificationExpiredFlow();
                    } else {
                        this.showSessionExpired.set(true);
                    }
                });
        }
    }

    ngOnDestroy(): void {
        this.clearTimer();
    }

    /** Initiates the Auth0 login flow. */
    public login(): void {
        this._authService.login();
    }

    /**
     * Detects an Auth0 password-reset success redirect by inspecting the `message`
     * query param. Auth0's exact wording can vary by tenant — match any message
     * mentioning "password" alongside reset/change/update keywords.
     *
     * @param message - Value of the `?message=` query param.
     * @returns True when the message indicates a successful password reset.
     */
    private isPasswordResetMessage(message: string | null): boolean {
        if (!message) return false;
        const m = message.toLowerCase();
        if (!m.includes('password')) return false;
        return m.includes('reset') || m.includes('updated') || m.includes('changed');
    }

    /**
     * Detects Auth0 rate-limit / blocked error returns. Used to short-circuit the
     * normal login UI and route the user to the dedicated `/block` page.
     *
     * @param error - Value of the `?error=` query param.
     * @param description - Value of the `?error_description=` query param.
     * @returns True when the error indicates the user has been throttled or blocked.
     */
    private isRateLimitedError(error: string | null, description: string | null): boolean {
        if (!error) return false;
        const code = error.toLowerCase();
        if (code === 'too_many_attempts' || code === 'too_many_requests') return true;
        if (code === 'mfa_invalid_credentials') return true;
        if (code === 'access_denied' && description) {
            const d = description.toLowerCase();
            if (d.includes('blocked') || d.includes('rate') || d.includes('too many')) return true;
        }
        return false;
    }

    /**
     * Maps an `error` query-param code to a user-facing message and reveals the error toast.
     *
     * @param code - Error code passed via the `?error=` query param (e.g. `auth_failed`).
     */
    private showAuthErrorToast(code: string): void {
        const messages: Record<string, string> = {
            auth_failed: 'Authentication failed. User not found or invalid credentials.',
        };
        this.errorToastMessage.set(messages[code] ?? 'Authentication failed. Please try again.');
        this.showErrorToast.set(true);
    }

    /** Hides the error toast when the user dismisses it or its duration elapses. */
    protected onErrorToastClosed(): void {
        this.showErrorToast.set(false);
    }

    /**
     * Disables the resend button immediately, sends the resend request, then re-fetches the
     * server-issued cooldown ttl so the timer is always in sync with backend state.
     */
    public resendEmail(): void {
        this.secondsLeft.set(this.initialSeconds());
        this._authService
            .resendEmailVerification()
            .pipe(
                switchMap(() =>
                    this._authService.getEmailVerificationExpiry<{ data: { ttl: number } }>(),
                ),
                takeUntilDestroyed(this._destroyRef),
            )
            .subscribe((res) => {
                const ttl = res?.data?.ttl ?? RESEND_COOLDOWN;
                if (ttl > 0) {
                    this.startTimer(ttl);
                } else {
                    this.timerExpired.set(true);
                }
            });
    }

    /**
     * Renders the verification-link-expired card and primes the resend cooldown timer
     * from the server-issued ttl. Only invoked once the session-status check confirms
     * the user still has an active backend session.
     */
    private startVerificationExpiredFlow(): void {
        this.showExpiredLink.set(true);
        this.hydrateEmailFromSession();
        this._authService
            .getEmailVerificationExpiry<{ data: { ttl: number } }>()
            .pipe(takeUntilDestroyed(this._destroyRef))
            .subscribe((res) => {
                const ttl = res?.data?.ttl ?? -2;
                if (ttl > 0) {
                    this.startTimer(ttl);
                } else {
                    // TTL -2: no cooldown key — button is immediately available
                    this.timerExpired.set(true);
                }
            });
    }

    /**
     * Starts (or restarts) the countdown timer.
     *
     * @param initialSeconds - Seconds to count down from. Defaults to `RESEND_COOLDOWN`.
     */
    private startTimer(initialSeconds = RESEND_COOLDOWN): void {
        this.clearTimer();
        this.timerExpired.set(false);
        this.initialSeconds.set(initialSeconds);
        this.secondsLeft.set(initialSeconds);
        this._timerInterval = setInterval(() => {
            const next = this.secondsLeft() - 1;
            if (next <= 0) {
                this.secondsLeft.set(0);
                this.timerExpired.set(true);
                this.clearTimer();
            } else {
                this.secondsLeft.set(next);
            }
        }, 1000);
    }

    /**
     * Reads the user's email address from sessionStorage user traits and stores it in `userEmail`.
     * Falls back to an empty string when no traits are present or parsing fails.
     */
    private hydrateEmailFromSession(): void {
        if (!isPlatformBrowser(this._platformId)) return;
        const raw = sessionStorage.getItem('ntv_user_traits');
        if (!raw) return;
        try {
            const traits = JSON.parse(raw) as { data?: { emailAddress?: string } };
            if (traits?.data?.emailAddress) {
                this.userEmail.set(traits.data.emailAddress);
            }
        } catch {
            // Malformed traits — leave userEmail empty so template falls back to "your email".
        }
    }

    /** Cancels the active countdown interval if one is running. */
    private clearTimer(): void {
        if (this._timerInterval !== null) {
            clearInterval(this._timerInterval);
            this._timerInterval = null;
        }
    }
}
